import React from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

export interface Column {
    id: string;
    label: React.ReactNode;
    sortable?: boolean;
    align?: 'left' | 'center' | 'right';
    width?: string;
    minWidth?: string;
}

export type SortDirection = 'asc' | 'desc' | null;

interface TableProps {
    columns: Column[];
    children?: React.ReactNode;
    className?: string;
    onSort?: (column: string, direction: SortDirection) => void;
    sortColumn?: string;
    sortDirection?: SortDirection;
    selectable?: boolean;
    selectedRows?: string[];
    onSelectAll?: (checked: boolean) => void;
    allRowsSelected?: boolean;
    indeterminate?: boolean;
    stickyHeader?: boolean;
    loading?: boolean;
    loadingRows?: number;
}

const LoadingSkeleton: React.FC<{ rows: number; columns: Column[]; selectable?: boolean }> = ({
    rows,
    columns,
    selectable
}) => {
    return (
        <>
            {Array.from({ length: rows }).map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-[#0A0B0E] border-b border-b-[#1A1D24]">
                    {selectable && (
                        <td className="w-12 px-4 py-3">
                            <div className="flex items-center">
                                <div className="w-4 h-4 bg-[#1A1D24] rounded animate-pulse" />
                            </div>
                        </td>
                    )}
                    {columns.map((column) => (
                        <td key={column.id} className="px-4 py-3">
                            <div className="space-y-2">
                                <div
                                    className="h-3 bg-[#1A1D24] rounded animate-pulse"
                                    style={{ width: `${Math.random() * 40 + 40}%` }}
                                />
                            </div>
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
};

export const Table: React.FC<TableProps> = ({
    columns,
    children,
    className = '',
    onSort,
    sortColumn,
    sortDirection,
    selectable,
    onSelectAll,
    allRowsSelected = false,
    indeterminate = false,
    stickyHeader = true,
    loading = false,
    loadingRows = 5,
}) => {
    const handleSort = (columnId: string) => {
        if (!onSort) return;

        const newDirection: SortDirection =
            sortColumn === columnId
                ? sortDirection === 'asc'
                    ? 'desc'
                    : sortDirection === 'desc'
                        ? null
                        : 'asc'
                : 'asc';

        onSort(columnId, newDirection);
    };

    const renderSortIcon = (columnId: string) => {
        if (sortColumn !== columnId) {
            return <div className="w-3 h-3" />;
        }

        return sortDirection === 'asc' ? (
            <ChevronUp className="w-3 h-3 text-[#00FFFF]" />
        ) : sortDirection === 'desc' ? (
            <ChevronDown className="w-3 h-3 text-[#00FFFF]" />
        ) : (
            <div className="w-3 h-3" />
        );
    };

    return (
        <div className={`relative w-full rounded-xl overflow-hidden bg-[#0A0B0E] border border-[#1A1D24] font-mono shadow-[0_0_30px_rgba(0,0,0,0.3)] ${className}`}>
            <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-full border-collapse">
                    <thead className={`bg-[#050505] border-b border-[#1A1D24] ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
                        <tr>
                            {selectable && (
                                <th scope="col" className="w-12 px-4 py-3 border-b border-[#1A1D24]">
                                    <div className="flex items-center">
                                        <input
                                            type="checkbox"
                                            className="cyber-checkbox"
                                            checked={allRowsSelected}
                                            ref={(input) => {
                                                if (input) {
                                                    input.indeterminate = indeterminate;
                                                }
                                            }}
                                            onChange={(e) => onSelectAll?.(e.target.checked)}
                                            disabled={loading}
                                        />
                                    </div>
                                </th>
                            )}
                            {columns.map((column) => (
                                <th
                                    key={column.id}
                                    scope="col"
                                    className={`
                                        px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 border-b border-[#1A1D24] transition-colors duration-200
                                        ${column.align === 'right' ? 'text-right' : ''}
                                        ${column.align === 'center' ? 'text-center' : ''}
                                        ${column.sortable && !loading ? 'cursor-pointer select-none hover:text-[#00FFFF]' : ''}
                                        ${loading ? 'opacity-50' : ''}
                                    `}
                                    style={{
                                        width: column.width,
                                        minWidth: column.minWidth,
                                    }}
                                    onClick={() => column.sortable && !loading && handleSort(column.id)}
                                >
                                    <div className={`flex items-center gap-2 ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start'}`}>
                                        <span>{column.label}</span>
                                        {column.sortable && renderSortIcon(column.id)}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-[#0A0B0E]">
                        {loading ? (
                            <LoadingSkeleton
                                rows={loadingRows}
                                columns={columns}
                                selectable={selectable}
                            />
                        ) : (
                            children
                        )}
                    </tbody>
                </table>
            </div>

            {/* Loading Overlay */}
            {loading && (
                <div className="absolute inset-0 bg-[#0A0B0E]/60 backdrop-blur-[2px] flex items-center justify-center z-20">
                    <div className="flex items-center gap-3 bg-[#12151C] border border-[#1A1D24] px-4 py-2 rounded-lg shadow-lg">
                        <div className="w-3.5 h-3.5 border-2 border-[#00FFFF]/30 border-t-[#00FFFF] rounded-full animate-spin" />
                        <span className="text-[10px] font-bold tracking-widest uppercase text-[#00FFFF]">Loading Telemetry...</span>
                    </div>
                </div>
            )}

            {/* Global Cyber Checkbox Styles baked into the component */}
            <style dangerouslySetInnerHTML={{
                __html: `
                .cyber-checkbox {
                    appearance: none !important;
                    -webkit-appearance: none !important;
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    width: 16px !important;
                    height: 16px !important;
                    border-radius: 4px !important;
                    position: relative !important;
                    cursor: pointer !important;
                    outline: none !important;
                    transition: all 0.2s ease !important;
                    display: inline-block !important;
                    vertical-align: middle !important;
                    margin: 0 !important;
                }

                .cyber-checkbox:hover:not(:disabled) {
                    border-color: #50A970 !important;
                }

                .cyber-checkbox:disabled {
                    opacity: 0.5 !important;
                    cursor: not-allowed !important;
                }

                .cyber-checkbox:checked,
                .cyber-checkbox:indeterminate {
                    background-color: rgba(80, 169, 112, 0.15) !important;
                    border-color: #50A970 !important;
                }

                .cyber-checkbox:checked::after {
                    content: '' !important;
                    position: absolute !important;
                    left: 4px !important;
                    top: 1px !important;
                    width: 5px !important;
                    height: 9px !important;
                    border: solid #50A970 !important;
                    border-width: 0 2px 2px 0 !important;
                    transform: rotate(45deg) !important;
                }

                .cyber-checkbox:indeterminate::after {
                    content: '' !important;
                    position: absolute !important;
                    left: 3px !important;
                    top: 6px !important;
                    width: 8px !important;
                    height: 2px !important;
                    background-color: #50A970 !important;
                    transform: none !important;
                    border: none !important;
                }
            `}} />
        </div>
    );
};

interface TableRowProps {
    children: React.ReactNode;
    className?: string;
    selected?: boolean;
    onSelect?: (checked: boolean) => void;
    rowId?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    variant?: 'default' | 'success' | 'warning' | 'danger' | 'critical';
    onClick?: () => void;
    disabled?: boolean;
}

export const TableRow: React.FC<TableRowProps> = ({
    children,
    className = '',
    selected,
    onSelect,
    rowId,
    onMouseEnter,
    onMouseLeave,
    variant = 'default',
    onClick,
    disabled = false,
}) => {
    // FIX: Using explicit border-l-[color] to prevent Tailwind from coloring the bottom border!
    const getVariantClasses = () => {
        switch (variant) {
            case 'critical':
                return 'border-l-2 border-l-[#FF3333] hover:bg-[#FF3333]/10';
            case 'danger':
                return 'border-l-2 border-l-[#F97316] hover:bg-[#F97316]/10';
            case 'warning':
                return 'border-l-2 border-l-[#EAB308] hover:bg-[#EAB308]/10';
            case 'success':
                return 'border-l-2 border-l-[#50A970] hover:bg-[#50A970]/10';
            default:
                return 'border-l-2 border-l-transparent hover:bg-[#12151C]';
        }
    };

    return (
        <tr
            className={`
                transition-all duration-200 ease-out border-b border-b-[#1A1D24]
                ${!disabled ? getVariantClasses() : 'border-l-2 border-l-transparent opacity-50'}
                ${selected ? 'bg-[#00FFFF]/5 border-l-2 !border-l-[#00FFFF]' : 'bg-[#0A0B0E]'}
                ${onClick && !disabled ? 'cursor-pointer' : ''}
                ${className}
            `}
            onMouseEnter={!disabled ? onMouseEnter : undefined}
            onMouseLeave={!disabled ? onMouseLeave : undefined}
            onClick={!disabled ? onClick : undefined}
        >
            {onSelect && (
                <td className="w-12 px-4 py-3 align-middle">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            className="cyber-checkbox"
                            checked={selected}
                            onChange={(e) => onSelect(e.target.checked)}
                            value={rowId}
                            onClick={(e) => e.stopPropagation()}
                            disabled={disabled}
                        />
                    </div>
                </td>
            )}
            {children}
        </tr>
    );
};

interface TableCellProps {
    children: React.ReactNode;
    className?: string;
    align?: 'left' | 'center' | 'right';
    variant?: 'default' | 'numeric' | 'status' | 'action';
}

export const TableCell: React.FC<TableCellProps> = ({
    children,
    className = '',
    align = 'left',
    variant = 'default',
}) => {
    return (
        <td
            className={`
                px-4 py-3 text-[12px] text-slate-300 align-middle
                ${align === 'right' ? 'text-right' : ''}
                ${align === 'center' ? 'text-center' : ''}
                ${variant === 'numeric' ? 'font-mono text-[#00FFFF]' : ''}
                ${className}
            `}
        >
            {children}
        </td>
    );
};

export default Table;